import { useBudgetOnboardingContext } from '@/components/cashflow/budget/BudgetOnboardingContext';
import { BudgetOnboardingModalLoading } from '@/components/cashflow/budget/BudgetOnboardingModalLoading';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { PillSwitcher } from '@/components/common/PillSwitcher';
import { useToasts } from '@/components/common/Toasts';
import { Header, Text } from '@/components/common/Typography';
import { useModal } from '@/components/ModalManager';
import { showConfetti } from '@/util/confetti';
import { logError } from '@/util/logger';
import { Divider } from '@chakra-ui/react';
import { BudgetFieldsFragment } from '@common/graphql/generated';
import { TIMEFRAME_OPTIONS, useConfirmViewProps } from '@common/react/budget';
import { TOASTS } from '@common/toasts';
import { BudgetOnboardingLayout } from './BudgetOnboardingLayout';

export const BudgetOnboardingConfirm = () => {
    const { budget, budgetLoading } = useBudgetOnboardingContext();

    if (budgetLoading || !budget) {
        return <BudgetOnboardingModalLoading />;
    }

    return <BudgetOnboardingConfirmInner budget={budget} />;
};

const BudgetOnboardingConfirmInner = ({
    budget,
}: {
    budget: BudgetFieldsFragment;
}) => {
    const modal = useModal();
    const { showSuccessToast, showErrorToast } = useToasts();

    const {
        timeframe,
        setTimeframe,
        handleSubmit,
        isSubmitting,
        incomeAmount,
        fixedAmount,
        lifestyleAmount,
        availableForSavingAmount,
    } = useConfirmViewProps({
        budget,
        onComplete: () => {
            showSuccessToast("You've successfully set up your budget!");
            showConfetti();
            modal.remove();
        },
        onError: (e) => {
            logError(e);
            showErrorToast(TOASTS.unexpectedError);
        },
    });

    return (
        <BudgetOnboardingLayout
            onContinue={handleSubmit}
            isContinueDisabled={false}
            isSubmitting={isSubmitting}
            buttonText="Confirm and finish"
            onClickEvent="Budget Onboarding Confirm Button Pressed"
        >
            <Header>Confirm your budget</Header>

            <FlexRow mt={8} justify="space-between" align="center">
                <Text fontWeight="semibold">
                    You&apos;ve set up your budget
                </Text>
                <PillSwitcher
                    options={TIMEFRAME_OPTIONS}
                    onChange={setTimeframe}
                    currentValue={timeframe}
                    colorScheme="blue"
                />
            </FlexRow>

            <FlexCol gap={3} mt={6}>
                <AmountRow
                    label="Income"
                    amount={incomeAmount}
                    amountColor="green.800"
                />
                <Divider borderStyle="dashed" mb={2} />
                <AmountRow label="Fixed" amount={fixedAmount} />
                <AmountRow label="Lifestyle" amount={lifestyleAmount} />
                <Divider borderStyle="dashed" my={2} />
                <AmountRow
                    label="Available for saving"
                    amount={availableForSavingAmount}
                />
            </FlexCol>
        </BudgetOnboardingLayout>
    );
};

const AmountRow = ({
    label,
    amount,
    amountColor,
}: {
    label: string;
    amount: string;
    amountColor?: string;
}) => (
    <FlexRow justify="space-between" align="center">
        <Text>{label}</Text>
        <Text color={amountColor}>{amount}</Text>
    </FlexRow>
);
